import LoadingButton from '@mui/lab/LoadingButton';
import { List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { Recommendation } from 'common/@types/recommendations';
import useModal from 'common/hooks/useModal';
import useStudentSelection, { Student } from 'common/hooks/useStudentSelection';
import { trpc } from 'common/utils/trpcAmplify';
import { useSnackbar } from 'notistack';
import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import BasicModal from '../modal/BasicModal';
import MinHeightTextarea from '../TextArea';

type Props = {};

const LessonUnblockModal = (props: Props) => {
  const [message, setMessage] = useState('');
  const [loading, _] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: unblockRecommendation } = trpc.recommendations.unblock.useMutation();
  const { isLessonUnblockModalOpen, toggleLessonUnblockModal, lessonUnblockData } = useModal();
  const [selectedRec, setSelectedRec] = useState<Recommendation | null>(null);
  const selectedStudents = useStudentSelection();
  const student: Student = selectedStudents[0];

  useEffect(() => {
    if (lessonUnblockData?.type === 'unblock' && lessonUnblockData?.unblockableRecs) {
      const lastUnblockedRec =
        lessonUnblockData.unblockableRecs[lessonUnblockData.unblockableRecs.length - 1];
      setSelectedRec(lastUnblockedRec);
    } else if (lessonUnblockData?.type === 'support') {
      if (lessonUnblockData.unblockableRecs?.length === 1) {
        setSelectedRec(lessonUnblockData.unblockableRecs[0]);
      } else {
        setSelectedRec(null);
      }
    }
  }, [lessonUnblockData]);

  const handleLessonUnblock = useCallback(
    async (msg: string) => {
      if (
        lessonUnblockData?.type === 'support' &&
        lessonUnblockData.unblockableRecs?.length > 1 &&
        !selectedRec
      ) {
        enqueueSnackbar('Please select a lesson for which you want to add supporting lesson.', {
          variant: 'error',
        });
        return;
      }

      if (!selectedRec && !lessonUnblockData?.recommendation) {
        enqueueSnackbar('No lesson selected', { variant: 'error' });
        return;
      }

      if (!student?.id || !msg) {
        enqueueSnackbar('Please add a message before unblocking', { variant: 'error' });
        return;
      }

      try {
        if (!lessonUnblockData) return;

        console.log('payload', {
          student_id: student.id,
          recommendation_id:
            selectedRec?.recommendation_id ?? lessonUnblockData?.recommendation?.recommendation_id,
          message: message,
          type: lessonUnblockData.type,
        });
        handleClose();
        enqueueSnackbar(
          `${
            lessonUnblockData.type === 'unblock'
              ? 'Lesson will be unblocked in a moment'
              : 'Support lessons will be added in a moment'
          }`,
          { variant: 'info' }
        );
        const response = await unblockRecommendation({
          student_id: student.id,
          recommendation_id:
            selectedRec?.recommendation_id ?? lessonUnblockData?.recommendation?.recommendation_id,
          subject: lessonUnblockData?.recommendation?.block,
          message: message,
          type: lessonUnblockData.type,
        });
        setTimeout(() => {
          if (response?.message) {
            enqueueSnackbar(
              `${
                lessonUnblockData.type === 'unblock'
                  ? 'Lesson unblocked successfully'
                  : 'Support lessons added successfully'
              }`,
              { variant: 'success' }
            );
          }
        }, 1000);
      } catch (error) {
        let errorMsg = '';
        if (lessonUnblockData?.type === 'unblock') {
          errorMsg = error.message;
        } else if (lessonUnblockData?.type === 'support') {
          errorMsg =
            'Supporting lessons are not available. Please click the help button in the bottom right corner to get support.';
        }

        enqueueSnackbar(errorMsg, { variant: 'error' });
      } finally {
        handleClose();
      }
    },
    [student, lessonUnblockData]
  );
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };
  const handleClose = () => {
    toggleLessonUnblockModal({
      recommendation: undefined,
      type: lessonUnblockData?.type,
      unblockableRecs: lessonUnblockData?.unblockableRecs || [],
    });
    setSelectedRec(null);
    setMessage('');
  };

  return (
    <BasicModal
      sx={{
        width: '550px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        '& .MuiBox-root': {
          overflowY: 'auto',
          maxHeight: '85vh',
          width: '100%',
          padding: '20px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        },
      }}
      open={isLessonUnblockModalOpen}
      onClose={handleClose}
    >
      <Typography component="h2" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'start' }}>
        {lessonUnblockData?.type === 'unblock'
          ? 'You should only unblock the next lesson if the student has completed it and is facing a technical issue.'
          : 'You should only add a support lesson if you are stuck on your current lesson.'}
      </Typography>
      {lessonUnblockData?.type === 'support' &&
        lessonUnblockData?.unblockableRecs &&
        lessonUnblockData.unblockableRecs.length > 1 && (
          <>
            <Typography sx={{ fontSize: '14px', fontWeight: 'bold', width: '100%' }}>
              Select the lesson you need support for:
            </Typography>
            <List
              sx={{
                width: '100%',
                bgcolor: 'background.paper',
                maxHeight: '200px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {lessonUnblockData.unblockableRecs.map((rec) => (
                <ListItemButton
                  key={rec.uuid}
                  onClick={() => setSelectedRec(rec)}
                  sx={{
                    border: '1px solid',
                    borderColor: selectedRec?.uuid === rec.uuid ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: selectedRec?.uuid === rec.uuid ? 'primary.light' : 'background.paper',
                    '&:hover': {
                      bgcolor: selectedRec?.uuid === rec.uuid ? 'primary.light' : 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={`${rec.name}`}
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: selectedRec?.uuid === rec.uuid ? 'bold' : 'normal',
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      <Typography
        sx={{
          fontSize: '14px',
          textAlign: 'start',
          width: '100%',
        }}
      >
        <b>{'Lesson Name: '}</b>
        {lessonUnblockData?.type === 'support' && lessonUnblockData.unblockableRecs?.length > 1
          ? selectedRec?.name || 'Please select a lesson'
          : selectedRec?.name || lessonUnblockData?.recommendation?.name}
      </Typography>
      <MinHeightTextarea
        value={message}
        onChange={handleChange}
        placeholder={
          lessonUnblockData?.type === 'unblock'
            ? 'Please describe why the student needs unblocking and confirm that you have verified lesson completion in the APP.\nReminders:\n1. IXL requires 100 SmartScore to master, Science may end before but the student needs to go back and get the 100.\n2. AlphaReading may not reach 100 smartscore.\n3. If you have doubts on mastery requirements, please confirm with Support on Slack.'
            : 'Please describe why you need to add a support lesson.'
        }
        minRows={5}
        style={{ marginBottom: '10px', fontSize: '15px' }}
      />
      <LoadingButton
        sx={{ width: '45%' }}
        loading={loading}
        disabled={loading}
        loadingPosition="start"
        variant="contained"
        color="info"
        onClick={() => handleLessonUnblock(message)}
      >
        {`${lessonUnblockData?.type === 'unblock' ? 'Unblock' : 'Add support'} lesson`}
      </LoadingButton>
    </BasicModal>
  );
};

export default memo(LessonUnblockModal);
